import { CollectionAsset, CollectionFolder } from 'editor/src/store/collections/types';
import { MediaElement, MediaImage } from 'editor/src/store/design/types';
import { PluginName } from 'editor/src/store/plugins/types';

export enum CollectionDisplayOption {
  Thumbnail,
  Dropdown,
  Color,
}

export interface AdvancedPersonalisationElementData {
  folderId: string;
  displayOption: CollectionDisplayOption;
  // Ids of the assets this element offers. When set, the element shows only this subset of the
  // folder pool (per-page curation); when absent, it shows the whole folder (legacy behaviour).
  assetIds?: string[];
  // Shared id across linked collection layers; one customer choice drives all of them by index.
  linkGroupId?: string;
  // Dropdown label overrides keyed by assetId; fallback is the asset name.
  assetLabels?: { [assetId: string]: string };
}

export function getCollectionAssetLabel(
  collectionInfo: AdvancedPersonalisationElementData,
  asset: CollectionAsset,
): string {
  return collectionInfo.assetLabels?.[asset.id] || asset.name;
}

export function getAdvPersoDataFromElement(element: MediaElement): AdvancedPersonalisationElementData | undefined {
  return (
    element.type === 'image' &&
    (element.plugins?.[PluginName.AdvancedPersonalisation] || element.plugins?.[PluginName.personalizationStudio])
  );
}

// Rebuilds the full plugins map with the new advanced-personalisation data written under both
// legacy and current plugin keys. Never mutates — returns a fresh object for use in an
// elementUpdate passed to updateMediaElementAction.
// TODO remove advancedPersonalizationPlugin after cleanup from apps n services.
export function buildAdvPersoPluginsUpdate(element: MediaImage, data: AdvancedPersonalisationElementData) {
  return {
    ...element.plugins,
    [PluginName.AdvancedPersonalisation]: data,
    [PluginName.personalizationStudio]: data,
  };
}

// Data for a collection (re)applied to an element. Re-applying the folder the element already uses
// (e.g. after adding a file to it) keeps the merchant's settings: display mode, link group and the
// labels of assets still in the folder. A different folder starts fresh.
export function getAppliedAdvPersoData(
  previous: AdvancedPersonalisationElementData | undefined,
  folder: CollectionFolder,
  assetIds?: string[],
): AdvancedPersonalisationElementData {
  const data: AdvancedPersonalisationElementData = {
    folderId: folder.id,
    displayOption: CollectionDisplayOption.Thumbnail,
  };
  if (assetIds?.length) {
    data.assetIds = assetIds;
  }
  if (!previous || previous.folderId !== folder.id) {
    return data;
  }

  data.displayOption = previous.displayOption;
  if (previous.linkGroupId) {
    data.linkGroupId = previous.linkGroupId;
  }
  const folderAssetIds = new Set(folder.assets.map((asset) => asset.id));
  const assetLabels = Object.fromEntries(
    Object.entries(previous.assetLabels ?? {}).filter(([assetId]) => folderAssetIds.has(assetId)),
  );
  if (Object.keys(assetLabels).length) {
    data.assetLabels = assetLabels;
  }
  return data;
}

// Assets an element offers: the element's curated subset (advPersoData.assetIds) resolved against
// the full folder pool, preserving folder order. Falls back to the whole folder when no subset is
// stored, so legacy elements keep showing every asset.
export function getElementCollectionAssets(
  folder: CollectionFolder,
  collectionInfo: AdvancedPersonalisationElementData,
): CollectionAsset[] {
  const { assetIds } = collectionInfo;
  if (!assetIds?.length) {
    return folder.assets;
  }
  const idSet = new Set(assetIds);
  return folder.assets.filter((asset) => idSet.has(asset.id));
}
